import React, { useContext, useRef } from "react"
import { useRecoilValue } from "recoil"
import { DimensionsContext, DimensionsProvider } from "../../../../../../../../Providers/DimensionsProvider"
import { annotationsAtom, canOpenAnnotationsModalAtom, annotationZOrderAtom } from "../../../../Atoms/Annotations"
import { selectedLayoutIdAtom } from "../../../../Atoms/Layout"
import { currentPatientFileInfoAtom } from "../../../../Atoms/PatientFile"
import { pageManagerRegistry } from "../../../../Data/PageManagerRegistry"
import { viewScaleRegistry } from "../../../../Data/ViewScaleRegistry"
import { useD3KeyboardShortcuts } from "../../../../Hooks/useD3KeyboardShortcuts"
import { hotkeyActions } from "../../../../Types/KeyboardShortcut"
import { useInProgressAnnotation } from "../../TimeSeriesGraphGroup/React/useInProgressAnnotation"
import { D3PersystEEGWindow } from "../D3/D3PersystEEGWindow"
import { PersystEEGImagePageManager } from "../../../../Data/PersystEEGImagePageManager"
import { PersystEEGWindowSettings } from "./PersystEEGWindowSettings"
import { PersystEEGWindowConfig, PersystEEGWindowJSON } from "../../../../Types/PersystWindow"
import { persystEEGAtom } from "../../../../Atoms/PersystWindow"
import { useD3CheckReloadData } from "../../../../Hooks/useD3CheckReloadData"
import { fileScaleRegistry } from "../../../../Data/FileScaleRegistry"
import { useD3Controller } from "../../../../Hooks/useD3Controller"
import { useSocketProvider } from "../../../../../../../../Providers/SocketProvider"
import { useEventReviewProvider } from "../../../../../../../../Providers/EventReviewProvider"
import { VisualizationComponent } from "../../../../VisualizationComponent"
import { MobergColumn, MobergRow } from "../../../../../../../../Moberg"
import { useAnnotationContextMenu } from "../../../ContextMenus/useAnnotationContextMenu"

export type PersystEEGWindowProps = {
	windowId: string
	json: PersystEEGWindowJSON
}

export const PersystEEGWindow = (props: PersystEEGWindowProps) => { 
	const { persystRecordingInfo } = useRecoilValue(currentPatientFileInfoAtom)

	if (!persystRecordingInfo) {
		return (
			<MobergColumn verticalAlign="center" expand={true}>
				<MobergRow horizontalAlign="center">
					<MobergColumn gap="16px" horizontalAlign="center">
						<h3 style={{ margin: 0 }}>Persyst is unavailable</h3>
					</MobergColumn>
				</MobergRow>
			</MobergColumn>
		)
	}

	return (
		<div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
			<div style={{ flex: 0 }}>
				<PersystEEGWindowSettings windowId={props.windowId} />
			</div>
			<div style={{ flex: 1 }}>
				<DimensionsProvider>
					<PersystEEGVisualization {...props} />
				</DimensionsProvider>
			</div>
		</div>
		
	)
}

export const PersystEEGVisualization = (props: PersystEEGWindowProps) => {
	const eventReviewProvider = useEventReviewProvider()
	const container = useRef<HTMLDivElement>(null)
	const { persystRecordingInfo } = useRecoilValue(currentPatientFileInfoAtom)
	const fileStartDate = persystRecordingInfo?.recordingStartDate ?? new Date()
	const fileEndDate = persystRecordingInfo?.recordingEndDate ?? new Date()
	const { getDataQuerySocket } = useSocketProvider()

	const selectedLayoutId = useRecoilValue(selectedLayoutIdAtom)
	const visualizationId = { layoutId: selectedLayoutId as string, windowId: props.windowId }
	const atom = persystEEGAtom(visualizationId)
	const config = useRecoilValue(atom)
	const enableOpeningAnnotationsModal = useRecoilValue(canOpenAnnotationsModalAtom)
	const annotationZOrder = useRecoilValue(annotationZOrderAtom(props.windowId))
	const { AnnotationContextMenu, openAnnotationContextMenu } = useAnnotationContextMenu(props.windowId)

	const dimensions = useContext(DimensionsContext)

    const annotations = useRecoilValue(annotationsAtom)
    const { inProgressAnnotation } = useInProgressAnnotation(props.windowId)

	const viewScale = viewScaleRegistry.get(visualizationId, VisualizationComponent.PERSYST_EEG, { fileStartDate, fileEndDate, viewDuration: props.json.viewDuration ?? 30 * 1000 })
	const [viewStart, viewEnd] = viewScale.domain()
	const viewDuration = viewEnd.getTime() - viewStart.getTime()

	const d3Controller = useD3Controller<D3PersystEEGWindow, PersystEEGWindowConfig, PersystEEGWindowJSON, PersystEEGImagePageManager>({
		atom,
		nodeRef: container,
		initProps: props.json,
		pageManager: pageManagerRegistry.get(visualizationId, VisualizationComponent.PERSYST_EEG, new PersystEEGImagePageManager(), getDataQuerySocket),
		props: {
			id: props.windowId,
			windowType: VisualizationComponent.PERSYST_EEG,
			annotations,
			inProgressAnnotation,
			viewScale,
			fileScale: fileScaleRegistry.get(visualizationId, VisualizationComponent.PERSYST_EEG, fileStartDate, fileEndDate),
			enableOpeningAnnotationsModal,
			currentlyReviewedAnnotation: eventReviewProvider.currentAnnotation,
			hideAnnotations: eventReviewProvider.hideAnnotations,
			annotationZOrder
		},
		d3ControllerConstructor(node, config, pageManager, callbacks) {
			return new D3PersystEEGWindow(node, config, pageManager, { ...callbacks, openAnnotationContextMenu, setSelectedAnnotation: () => {} })
		},
		extractJSON(config) {
            return {
                viewDuration: viewDuration,
                playbackSpeed: config.playbackSpeed,
				montage: config.montage,
				sensitivityMicroVolts: config.sensitivityMicroVolts,
				LFF: config.LFF,
				HFF: config.HFF,
				notch: config.notch,
				isArtifactReductionEnabled: config.isArtifactReductionEnabled
            }
        },
	})

    useD3KeyboardShortcuts({
        d3Controller,
        windowId: props.windowId,
        shortcuts: [
            hotkeyActions.NEXT_PAGE,
			hotkeyActions.PREVIOUS_PAGE,
			hotkeyActions.UPDATE_CUSTOM_ANNOTATION_MARKERS,
			hotkeyActions.CANCEL_ANNOTATION,
			hotkeyActions.PLAY_PAUSE
        ]
    })

	useD3CheckReloadData({
        d3Controller,
        clearDataIfChanges: {
			dimensions,
            viewDuration,
			montage: config.montage,
			sensitivityMicrovolts: config.sensitivityMicroVolts,
			isArtifactReductionEnabled: config.isArtifactReductionEnabled,
			LFF: config.LFF,
			HFF: config.HFF,
			notch: config.notch
        }
    })

	return (
		<div ref={container} style={{ height: "100%" }}>
			{AnnotationContextMenu}
		</div>
	)
}
